import { NoAlgoliaSearchResults } from "@components/AlgoliaCustomComponents";
import { MIN_SEARCH_CHARS } from "@components/AlgoliaCustomComponents/CustomSearchBox/CustomSearchBox";
import analyticstracker from "@jmc/analyticstracker";
import { EventTypes } from "@jmc/core/src/types/EventTypes";
import { Button } from "@jmc/solid-design-system/src/components/atoms/Button/Button";
import { CSSGrid } from "@jmc/solid-design-system/src/components/atoms/CSSGrid/CSSGrid";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import {
    CurrentRefinementsConnectorParamsItem,
    CurrentRefinementsConnectorParamsRefinement,
} from "instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCurrentRefinements, useHits, UseHitsProps, useRefinementList, useSearchBox } from "react-instantsearch";

import RecipeHit from "../RecipeHit/RecipeHit";
import { RecipeHit as RecipeHitType } from "../types/RecipeHit";
import style from "./style.module.scss";

const RESULTS_PER_LOAD = 12;

interface PropTypes extends UseHitsProps {
    placement: string;
}

const CustomRecipeHits = (props: PropTypes): JSX.Element => {
    const { placement } = props;
    const { hits } = useHits(props);
    const { refine, query, clear } = useSearchBox();
    const { items } = useCurrentRefinements();
    const { t } = useTranslation();
    const ref = useRef(null);
    const { jnjFullBranded } = useJnjBranding();

    // get the filtered attributes for tracking purpose
    const filteredItems: CurrentRefinementsConnectorParamsRefinement[] = [];
    items?.forEach((item: CurrentRefinementsConnectorParamsItem) => {
        item?.refinements?.forEach((selectedItem) => {
            filteredItems.push(selectedItem);
        });
    });

    const getFilterAttributes = (filterAttributes: CurrentRefinementsConnectorParamsRefinement[]): string => {
        const filteredItemNames: { [key: string]: (string | number)[] } = {};
        for (const item of filterAttributes) {
            const attribute = item?.attribute;
            const value = item?.value;
            if (!filteredItemNames[attribute]) {
                filteredItemNames[attribute] = [];
            }
            filteredItemNames[attribute].push(value);
        }
        const formattedFilters = [];
        for (const attribute in filteredItemNames) {
            formattedFilters.push(`${attribute}:${filteredItemNames[attribute].join(", ")}`);
        }
        return formattedFilters.join("|");
    };

    const [pageNumber, setPageNumber] = useState(1);

    const totalHits = query?.length >= MIN_SEARCH_CHARS || filteredItems?.length ? hits?.length : 0;

    const refinementTypes = ["specialty", "dietary_wishes", "course", "difficulty"];

    const refinements = refinementTypes.map((refinement) => {
        const { refine } = useRefinementList({ attribute: refinement });
        return { type: refinement, refine };
    });

    useEffect(() => {
        if (query?.length >= MIN_SEARCH_CHARS || filteredItems?.length) {
            const tracking = setTimeout(() => {
                analyticstracker().trackEvent({
                    event: EventTypes.SEARCH_RESULT,
                    info: {
                        keyword: query || "unset",
                        type: "recipe",
                        resultcategory: "unset",
                        resultcount: totalHits,
                        filter: getFilterAttributes(filteredItems) || "",
                    },
                });
            }, 1000);

            return (): void => {
                clearTimeout(tracking);
            };
        }
    }, [totalHits, query, filteredItems]);

    const loadMore = (currentPage: number): void => {
        const nextItem = currentPage * RESULTS_PER_LOAD + 1;
        setPageNumber(currentPage + 1);
        setTimeout(() => {
            ref?.current && ref?.current?.querySelector(`[data-recipe-id="${nextItem}"]`)?.focus();
        }, 100);
    };

    const recipeArray = hits.slice(0, pageNumber * RESULTS_PER_LOAD).map((hit) => {
        return (
            <RecipeHit
                key={hit?.__position}
                hit={hit as RecipeHitType}
                placement={placement}
                refinements={refinements}
                hoverOnTags={true}
            />
        );
    });

    if (recipeArray.length > 0) {
        return (
            <div ref={ref}>
                <CSSGrid
                    container
                    width={jnjFullBranded ? undefined : "20rem"}
                    size={jnjFullBranded ? undefined : "auto"}
                    overview={jnjFullBranded}
                    gap={24}
                >
                    {recipeArray}
                </CSSGrid>
                <div className={style.recipePager}>
                    <Typography color="light" variant="body" data-test-id="Counter">
                        {pageNumber * RESULTS_PER_LOAD < hits.length &&
                            t("Showing", { ns: "recipe" }) +
                                ` ${pageNumber * RESULTS_PER_LOAD} ` +
                                t("of", { ns: "recipe" }) +
                                ` ${hits.length} ` +
                                t("results", { ns: "recipe" })}
                        {pageNumber * RESULTS_PER_LOAD >= hits.length &&
                            t("Showing", { ns: "recipe" }) +
                                ` ${hits.length} ` +
                                t("of", { ns: "recipe" }) +
                                ` ${hits.length} ` +
                                t("results", { ns: "recipe" })}
                    </Typography>
                    {pageNumber * RESULTS_PER_LOAD < hits.length && (
                        <Button variant={jnjFullBranded ? "outlined" : undefined} onClick={() => loadMore(pageNumber)}>
                            {t("Show more", { ns: "recipe" })}
                        </Button>
                    )}
                </div>
            </div>
        );
    } else {
        return <NoAlgoliaSearchResults refine={refine} query={query} clear={clear} />;
    }
};

export default CustomRecipeHits;
